import STATUS from '@shared/constants/StatusConstants';
import ADS_PLATFORMS from '@shared/constants/AdsPlatformsConstants';
import { DEFAULT_METRICS } from '@shared/constants/MetricsTypeConstants';
import { limitNumberOfCharacters, convertAllCapsSnakeCaseToTitleCase, slugify } from '@shared/utils/strings';
import {
    MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
    MANUAL_STRATEGY_TYPE,
    TARGET_STRATEGY_TYPE,
    TARGET_IMPRESSION_SHARE,
} from '@domain/segments/segments-modal';
import tagsGroupConstants from '@shared/constants/overview/TagsGroupConstants';

const paused = STATUS.PAUSED.toLowerCase();
const enabled = STATUS.ENABLED.toLowerCase();

const isEnabled = campaign => campaign.status === enabled;

const isFacebookAds = campaign => campaign.platform === tagsGroupConstants.FACEBOOK_ADS;

const hasMetrics = campaign => Object.keys(DEFAULT_METRICS).reduce(
    (previous, metric) => (
        previous || (
            metric in campaign
            && Boolean(campaign[metric])
            && campaign[metric] !== 0)),
    false,
);

export const isActive = campaign => isEnabled(campaign) || hasMetrics(campaign);

export const toggleStatusValue = (
    status,
    caseIsPaused = enabled,
    caseIsEnabled = paused,
) => (status === paused ? caseIsPaused : caseIsEnabled);

const isCampaignChanged = (campaign, campaignId, subLevelId) => {
    if (subLevelId) {
        return campaign.campaign_id === campaignId && campaign.sublevel_id === subLevelId;
    }
    return campaign.campaign_id === campaignId;
};

export const editValueBasedOnField = (field, fieldValue) => (field === 'status'
    ? toggleStatusValue(fieldValue) : fieldValue);

export const toggleStatusBasedOnPlatfom = (status, platform) => (
    platform === ADS_PLATFORMS.FACEBOOK_ADS
        ? toggleStatusValue(status, STATUS.ACTIVE.toUpperCase(), STATUS.PAUSED)
        : toggleStatusValue(status, STATUS.ENABLED, STATUS.PAUSED));

export const formatFieldValueToApi = (field, fieldValue, platform) => (field === 'status'
    ? toggleStatusBasedOnPlatfom(fieldValue, platform)
    : fieldValue);

export const editCampaign = (
    data, campaignId, subLevelId, field, fieldValue, loading,
) => Object.values(data.items).map((campaign) => {
    if (isCampaignChanged(campaign, campaignId, subLevelId)) {
        return {
            ...campaign,
            loading,
            [field]: editValueBasedOnField(field, fieldValue),
        };
    }

    return campaign;
});

const isTagEqualSlugGroup = groupSlug => tag => tag.group_slug !== groupSlug;

const filterCampaignTags = groupSlug => campaign => ({
    ...campaign,
    tags: campaign.tags?.filter(isTagEqualSlugGroup(groupSlug)),
});

export const deleteTagGroupBySlug = (campaigns, groupSlug) => campaigns
    .map(filterCampaignTags(groupSlug));

export const formatBidStrategyForCampaigns = (campaigns) => {
    const { items } = campaigns;
    const formattedCampaignsWithBidStrategy = items.map(campaign => ({
        ...campaign,
        bid_strategy_type: getObjectiveForBidStrategy(campaign),
        is_cbo: !!campaign.bid_strategy_type,
    }));

    return {
        ...campaigns,
        items: formattedCampaignsWithBidStrategy,
    };
};

const getObjectiveForBidStrategy = (campaign) => {
    const isFacebookAdsCampaign = isFacebookAds(campaign);
    const isObjective = (campaign.objective && isFacebookAdsCampaign);

    const isBidStrategyType = (campaign.bid_strategy_type && !isFacebookAdsCampaign);

    const isTargetImpressionShare = campaign.bid_strategy_type === '--';

    if (isTargetImpressionShare) {
        return TARGET_IMPRESSION_SHARE;
    }

    return (
        (isObjective || isBidStrategyType)
        && limitNumberOfCharacters(
            getStrategyForCampaign(campaign, isFacebookAdsCampaign),
            MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
        )
    );
};

const formatStrategyTypeForGoogle = ({
    bid_strategy_type: bidStrategyType,
}) => MANUAL_STRATEGY_TYPE[bidStrategyType]
    || TARGET_STRATEGY_TYPE[bidStrategyType]
    || convertAllCapsSnakeCaseToTitleCase(bidStrategyType);

// @TODO: Discutir a escalabalidade desse tratamento para casos futuros
// @psdaniel, @gustavosilvaf, @PedroDFigueiredo
export const getStrategyForCampaign = (campaign, isFacebook) => (
    isFacebook
        ? convertAllCapsSnakeCaseToTitleCase(campaign.objective)
        : formatStrategyTypeForGoogle(campaign)
);

export const updateTagGroupByCluster = (campaigns, {
    tagGroupId,
    newTagName,
    previousTagName,
}) => campaigns.map(campaign => ({
    ...campaign,
    tags: campaign.tags.map(tag => (
        (tag.group_id === tagGroupId && tag.tag.label === previousTagName)
            ? ({
                ...tag,
                tag: {
                    value: slugify(newTagName),
                    label: newTagName,
                },
            })
            : tag)),
}));
